import React, { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import axios from "axios";
// import ChatNotification from "../ChatNotification/ChatNotification";
import "./Chat.css";
import useAuth from "../../Auth/useAuth";
import { useChatNotifications } from "../../Auth/ChatNotificationContext";
import {
  HiOutlineLogout,
  HiUserGroup,
  HiDotsVertical,
  // HiBell,
  HiOutlineTrash,
  HiTrash,
  HiCog,
} from "react-icons/hi";
import AlertModal from "../AlertModal/AlertModal";
import config from "../../config";

const baseUrl = config.BASE_URL;

const Chat = () => {
  const { user, isAuthenticated, decrementUnreadCount, refreshUnreadCount } =
    useAuth();
  const {
    chats,
    fetchChatsWithUnreadCount,
    markChatAsRead,
    handleNewMessage,
    // notificationSettings,
    // updateNotificationSetting,
    addNewChat,
  } = useChatNotifications();

  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  // Group chat states
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Group management states
  const [showGroupDetails, setShowGroupDetails] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [groupSearchTerm, setGroupSearchTerm] = useState("");

  const [notification, setNotification] = useState(null);
  const [notificationSettings, setNotificationSettings] = useState({});
  const [muting, setMuting] = useState(false);
  // ADD this state near your other useState declarations
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const messagesContainerRef = useRef(null);

  // file availability state
  const [unavailableFiles, setUnavailableFiles] = useState([]);

  //menu options
  const [showMenu, setShowMenu] = useState(false);

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const fetchTimeoutRef = useRef(null);
  const [isInitialLoad, setIsInitialLoad] = useState(false);
  const previousScrollHeightRef = useRef(0);

  // Add modal state
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  // Modal handler functions
  const showModal = (title, message, type = "info") => {
    setModalConfig({
      isOpen: true,
      title,
      message,
      type,
    });
  };

  const closeModal = () => {
    setModalConfig((prev) => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!socket || !user) return;

    const handleChatCreated = (newChat) => {
      console.log("New chat created:", newChat);

      // Add the new chat to the list of chats
      fetchChatsWithUnreadCount();

      // If it's a group chat and current user is part of it, join the socket.io room
      if (newChat.isGroup) {
        socket.emit("joinGroup", { chatId: newChat._id });
      }
    };

    const handleUserLeftGroup = (data) => {
      console.log("User left group:", data);

      if (data.userId === user.username) {
        fetchChatsWithUnreadCount();
        if (selectedChat && selectedChat._id === data.chatId) {
          setSelectedChat(null);
        }
      } else {
        if (selectedChat && selectedChat._id === data.chatId) {
          const systemMessage = {
            _id: `system-${Date.now()}`,
            chatId: data.chatId,
            senderId: "System",
            content: `${data.userId} left the group`,
            timestamp: new Date(),
            read: true,
            systemEvent: "userLeft",
            userId: data.userId,
          };

          setMessages((prev) => [...prev, systemMessage]);

          // Update the chat participants and creator if transferred
          setSelectedChat((prev) => ({
            ...prev,
            participants: prev.participants.filter((p) => p !== data.userId),
            ...(data.newCreator && { creator: data.newCreator }),
          }));

          // Add creator transfer message if applicable
          if (data.newCreator) {
            const creatorMessage = {
              _id: `system-creator-${Date.now()}`,
              chatId: data.chatId,
              senderId: "System",
              content: `${data.newCreator} is now the group creator.`,
              timestamp: new Date(),
              read: true,
              systemEvent: "creatorTransfer",
            };
            setMessages((prev) => [...prev, creatorMessage]);
          }

          if (showGroupDetails) {
            fetchAvailableUsers();
          }
          fetchChatsWithUnreadCount();
        }
      }
    };

    const handleUserRemovedFromGroup = (data) => {
      console.log("User removed from group:", data);

      if (data.removedUserId === user.username) {
        if (selectedChat && selectedChat._id === data.chatId) {
          const systemMessage = {
            _id: `system-${Date.now()}`,
            chatId: data.chatId,
            senderId: "System",
            content: `You were removed from the group by ${data.removedBy}`,
            timestamp: new Date(),
            read: true,
            systemEvent: "userRemoved",
            removedBy: data.removedBy,
          };

          setMessages((prev) => [...prev, systemMessage]);
        }

        setTimeout(() => {
          fetchChatsWithUnreadCount();
          if (selectedChat && selectedChat._id === data.chatId) {
            setSelectedChat(null);
          }
        }, 1000);
      } else {
        if (selectedChat && selectedChat._id === data.chatId) {
          const systemMessage = {
            _id: `system-${Date.now()}`,
            chatId: data.chatId,
            senderId: "System",
            content: `${data.removedUserId} was removed from the group by ${data.removedBy}`,
            timestamp: new Date(),
            read: true,
            systemEvent: "userRemoved",
            removedBy: data.removedBy,
            userId: data.removedUserId,
          };

          setMessages((prev) => [...prev, systemMessage]);

          // Update participants and creator
          setSelectedChat((prev) => ({
            ...prev,
            participants: prev.participants.filter(
              (p) => p !== data.removedUserId,
            ),
            ...(data.newCreator && { creator: data.newCreator }),
          }));

          // Add creator transfer message if applicable
          if (data.newCreator) {
            const creatorMessage = {
              _id: `system-creator-${Date.now()}`,
              chatId: data.chatId,
              senderId: "System",
              content: `${data.newCreator} is now the group creator.`,
              timestamp: new Date(),
              read: true,
              systemEvent: "creatorTransfer",
            };
            setMessages((prev) => [...prev, creatorMessage]);
          }

          if (showGroupDetails) {
            fetchAvailableUsers();
          }
        }
      }
    };
    // Handle user added to a group
    const handleUserAddedToGroup = (data) => {
      console.log("User added to group:", data);

      // If the current user was added to the group
      if (data.addedUserId === user.username) {
        // Refresh chats to include the new group
        fetchChatsWithUnreadCount();

        // Add system message if this chat is open
        if (selectedChat && selectedChat._id === data.chatId) {
          const systemMessage = {
            _id: `system-${Date.now()}`,
            chatId: data.chatId,
            senderId: "System",
            content: `You were added to the group by ${data.addedBy}`,
            timestamp: new Date(),
            read: true,
          };

          setMessages((prev) => [...prev, systemMessage]);
        }
      } else {
        // If another user was added to the group
        if (selectedChat && selectedChat._id === data.chatId) {
          // Add system message
          const systemMessage = {
            _id: `system-${Date.now()}`,
            chatId: data.chatId,
            senderId: "System",
            content: `${data.addedUserId} was added to the group by ${data.addedBy}`,
            timestamp: new Date(),
            read: true,
          };

          setMessages((prev) => [...prev, systemMessage]);

          // Update the chat participants IMMEDIATELY
          setSelectedChat((prev) => ({
            ...prev,
            participants: [...prev.participants, data.addedUserId],
          }));

          // Refresh available users in the group management modal
          if (showGroupDetails) {
            fetchAvailableUsers();
          }
        }
      }
    };

    // Handle notification when current user is added to a group
    const handleUserAddedToGroupNotification = (data) => {
      console.log("Current user added to group:", data);

      // Refresh chats to include the new group
      fetchChatsWithUnreadCount();

      // If this chat is currently selected, add system message
      if (selectedChat && selectedChat._id === data.chatId) {
        const systemMessage = {
          _id: `system-${Date.now()}`,
          chatId: data.chatId,
          senderId: "System",
          content: `You were added to the group by ${data.addedBy}`,
          timestamp: new Date(),
          read: true,
        };

        setMessages((prev) => [...prev, systemMessage]);
      }
    };

    // Handle individual chat deletion (soft delete)
    const handleChatDeleted = (data) => {
      console.log("Chat deleted for user:", data);

      // Remove the chat from the list
      fetchChatsWithUnreadCount();

      // If this was the selected chat, deselect it
      if (selectedChat && selectedChat._id === data.chatId) {
        setSelectedChat(null);
      }
    };

    // Add these new handlers to your socket useEffect
    // const handleChatCleared = (data) => {
    //   console.log("Chat cleared:", data);
    //   if (selectedChat && selectedChat._id === data.chatId) {
    //     const systemMessage = {
    //       _id: `system-${Date.now()}`,
    //       chatId: data.chatId,
    //       senderId: "System",
    //       content: `Chat was cleared by ${data.clearedBy}`,
    //       timestamp: new Date(),
    //       read: true,
    //       systemEvent: "chatCleared",
    //     };
    //     setMessages([systemMessage]); // Replace all messages with just the system message
    //   }
    // };

    const handleChatMessagesCleared = (data) => {
      console.log("Chat messages cleared for user:", data);
      if (selectedChat && selectedChat._id === data.chatId) {
        // Replace messages with just the system message
        setMessages([data.systemMessage]);

        // Refresh chats to update unread counts
        fetchChatsWithUnreadCount();
      }
    };

    // Update handleNewUserRegistered to use the NEW USER received data
    const handleNewUserRegistered = (data) => {
      console.log("New user registered:", data);

      // Extract the user from the data
      const newUser = data.user || data;

      // Ensure we have a valid username
      if (!newUser || !newUser.username) {
        console.error("Invalid user data received:", data);
        return;
      }

      setUsers((prevUsers) => {
        // Check if user already exists
        const userExists = prevUsers.some(
          (u) => u && u.username === newUser.username,
        );

        if (!userExists) {
          console.log("Adding new user to list:", newUser.username);
          return [...prevUsers, newUser];
        }
        return prevUsers;
      });
    };

    socket.on("chatCreated", handleChatCreated);
    socket.on("userLeftGroup", handleUserLeftGroup);
    socket.on("userRemovedFromGroup", handleUserRemovedFromGroup);
    socket.on("userAddedToGroup", handleUserAddedToGroup);
    socket.on("chatDeleted", handleChatDeleted);
    socket.on(
      "userAddedToGroupNotification",
      handleUserAddedToGroupNotification,
    );
    // socket.on("chatCleared", handleChatCleared);
    socket.on("chatMessagesCleared", handleChatMessagesCleared);
    socket.on("userListUpdated", handleNewUserRegistered);

    return () => {
      socket.off("chatCreated", handleChatCreated);
      socket.off("userLeftGroup", handleUserLeftGroup);
      socket.off("userRemovedFromGroup", handleUserRemovedFromGroup);
      socket.off("userAddedToGroup", handleUserAddedToGroup);
      socket.off("chatDeleted", handleChatDeleted);
      socket.off(
        "userAddedToGroupNotification",
        handleUserAddedToGroupNotification,
      );
      // socket.off("chatCleared", handleChatCleared);
      socket.off("chatMessagesCleared", handleChatMessagesCleared);
      socket.off("userListUpdated", handleNewUserRegistered);
    };
  }, [socket, user, selectedChat]);

  useEffect(() => {
    const checkFiles = async () => {
      const missing = [];

      for (const msg of messages) {
        if (msg.file?.fileName) {
          try {
            await axios.head(`${baseUrl}/chat/download/${msg.file.fileName}`, {
              withCredentials: true,
            });
          } catch (error) {
            if (error.response && error.response.status === 404) {
              missing.push(msg.file.fileName);
            }
          }
        }
      }

      setUnavailableFiles(missing);
    };

    if (messages.length) {
      checkFiles();
    }
  }, [messages]);

  useEffect(() => {
    if (selectedChat && isAuthenticated) {
      try {
        // Reset states for new chat
        setMessages([]);
        setIsInitialLoad(true);
        setShouldAutoScroll(true);
        setHasMoreMessages(true);
        setIsLoadingMore(false);
        previousScrollHeightRef.current = 0;

        // Clear any pending fetch timeouts
        if (fetchTimeoutRef.current) {
          clearTimeout(fetchTimeoutRef.current);
        }

        fetchMessages(selectedChat._id);
        markChatAsRead(selectedChat._id);
      } catch (error) {
        console.error("Error selecting chat:", error);
        // If there's an error, deselect the chat to prevent crashes
        setSelectedChat(null);
      }
    }
  }, [selectedChat?._id, isAuthenticated]);

  //useEffect to initialize notification settings
  useEffect(() => {
    if (chats.length > 0) {
      const initialSettings = {};
      chats.forEach((chat) => {
        // Handle both object and boolean notification settings
        if (typeof chat.notifications === "object") {
          initialSettings[chat._id] =
            chat.notifications[user.username] !== false;
        } else {
          initialSettings[chat._id] = chat.notifications !== false;
        }
      });
      setNotificationSettings(initialSettings);
    }
  }, [chats, user]);

  useEffect(() => {
    if (!socket || !user) return;

    const handleNotification = (notificationData) => {
      // Don't play sound for own messages
      if (notificationData.senderId === user.username) return;

      // Check if notifications are enabled for this chat
      const isNotificationEnabled =
        notificationSettings[notificationData.chatId] !== false;

      if (isNotificationEnabled) {
        console.log("Playing notification sound");
        // Play sound
        const audio = new Audio("/sounds/notification.mp3");
        audio.volume = 0.3;
        audio.play().catch(console.error);

        // Show notification toast
        setNotification(`New message from ${notificationData.senderId}`);
        setTimeout(() => setNotification(null), 3000);
      }
    };

    socket.on("notification", handleNotification);
    return () => socket.off("notification", handleNotification);
  }, [socket, user, notificationSettings]);

  // Initialize socket connection
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const token = localStorage.getItem("token");
    const newSocket = io("https://saleseco.mindfuturetechsupport.com", {
      auth: {
        token: token,
      },
      withCredentials: true,
    });

    newSocket.on("connect", () => {
      console.log("Connected to server");
      setIsConnected(true);
      newSocket.emit("register", { userId: user.username });
    });

    newSocket.on("disconnect", () => {
      console.log("Disconnected from server");
      setIsConnected(false);
    });

    newSocket.on("receiveMessage", (message) => {
      console.log("New message received:", message);

      handleNewMessage(message);

      if (selectedChat && message.chatId === selectedChat._id) {
        const shouldShowMessage = shouldShowMessageToUser(
          message,
          selectedChat,
          user.username,
        );

        if (shouldShowMessage) {
          setMessages((prev) => {
            // For messages from other users, just add them normally
            if (message.senderId !== user.username) {
              // Check if message already exists to prevent duplicates
              const existingMessageIndex = prev.findIndex(
                (msg) => msg._id === message._id,
              );

              if (existingMessageIndex !== -1) {
                // Update existing message
                const updatedMessages = [...prev];
                updatedMessages[existingMessageIndex] = message;
                return updatedMessages;
              } else {
                // If not, it's a new message from another user, so add it
                return [...prev, message];
              }
            }

            // For our own messages, check if we already have an optimistic version
            if (message.senderId === user.username) {
              // Look for optimistic message with matching content and sender
              const tempMessageIndex = prev.findIndex(
                (msg) =>
                  msg._id &&
                  msg._id.startsWith("temp-") &&
                  msg.senderId === user.username &&
                  msg.content === message.content,
              );

              if (tempMessageIndex !== -1) {
                // Replace the optimistic message with the real one from server
                const updatedMessages = [...prev];
                updatedMessages[tempMessageIndex] = message;
                return updatedMessages;
              } else {
                // If no optimistic message found, check if message already exists
                const existingMessageIndex = prev.findIndex(
                  (msg) => msg._id === message._id,
                );

                if (existingMessageIndex === -1) {
                  // Add the message if it doesn't exist
                  return [...prev, message];
                } else {
                  // Update existing message
                  const updatedMessages = [...prev];
                  updatedMessages[existingMessageIndex] = message;
                  return updatedMessages;
                }
              }
            }

            return prev;
          });
        }

        // Mark as read if it's from the other user
        if (message.senderId !== user.username) {
          markAsRead([message._id]);
        }
      }
    });

    newSocket.on("userTyping", (data) => {
      if (data.chatId === selectedChat?._id) {
        setTypingUsers((prev) => ({
          ...prev,
          [data.userId]: data.isTyping,
        }));

        setTimeout(() => {
          setTypingUsers((prev) => ({
            ...prev,
            [data.userId]: false,
          }));
        }, 2000);
      }
    });

    newSocket.on("messagesRead", (data) => {
      if (data.chatId === selectedChat?._id) {
        setMessages((prev) =>
          prev.map((msg) =>
            data.messageIds.includes(msg._id)
              ? {
                  ...msg,
                  read: true,
                  readBy: [...(msg.readBy || []), data.readBy],
                }
              : msg,
          ),
        );
      }
    });

    newSocket.on("messageSent", (data) => {
      console.log("Message sent confirmation:", data);
      // The receiveMessage handler will handle replacing the optimistic message
    });

    newSocket.on("messageError", (data) => {
      console.error("Error sending message:", data.error);
      // Remove the optimistic message on error
      if (data.tempId) {
        setMessages((prev) => prev.filter((msg) => msg._id !== data.tempId));
      }
    });

    newSocket.on("chatCleared", (data) => {
      console.log("Chat cleared:", data);
      if (selectedChat && selectedChat._id === data.chatId) {
        // The backend will send the system message, so we don't need to create it here
        // Just clear the messages
        setMessages([]);

        // Refresh chats to update the list
        fetchChatsWithUnreadCount();
      }
    });

    newSocket.on("clearChatSuccess", (data) => {
      console.log("Chat cleared successfully:", data);
      // UI is already updated optimistically
    });

    newSocket.on("clearChatError", (data) => {
      console.error("Error clearing chat:", data.error);
      showModal("Error", `Failed to clear chat: ${data.error}`, "error");
      // Refresh messages to restore UI
      if (selectedChat) {
        fetchMessages(selectedChat._id);
      }
    });

    // In your socket handlers, add success/error handling for clear chat
    newSocket.on("clearChatSuccess", (data) => {
      console.log("Chat cleared successfully:", data);
      // Refresh chats to update the list
      fetchChatsWithUnreadCount();
    });

    newSocket.on("clearChatError", (data) => {
      console.error("Error clearing chat:", data.error);
      showModal("Error", `Failed to clear chat: ${data.error}`, "error");
      // Refresh messages to restore UI
      if (selectedChat) {
        fetchMessages(selectedChat._id);
      }
    });

    setSocket(newSocket);
    return () => newSocket.close();
  }, [isAuthenticated, user, selectedChat]);

  // Fetch chats on component mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchChatsWithUnreadCount();
      fetchUsers(); // This will fetch all users data
    }
  }, [isAuthenticated]);

  // Update the useEffect for scrolling - replace the existing one around line 405:
  useEffect(() => {
    const container = messagesContainerRef.current;

    if (isInitialLoad && container) {
      // For initial load, scroll to bottom immediately
      setTimeout(() => {
        container.scrollTop = container.scrollHeight;
        setIsInitialLoad(false);
      }, 0);
    } else if (
      !isInitialLoad &&
      previousScrollHeightRef.current > 0 &&
      container
    ) {
      // For loading older messages, restore scroll position
      const newScrollHeight = container.scrollHeight;
      const scrollDifference =
        newScrollHeight - previousScrollHeightRef.current;
      container.scrollTop = scrollDifference;
      previousScrollHeightRef.current = 0; // Reset
    } else if (shouldAutoScroll && container) {
      // For auto-scroll when new messages arrive
      scrollToBottom();
    }
  }, [messages, shouldAutoScroll, isInitialLoad]);

  // Group management - fetch available users when opening group details
  useEffect(() => {
    if (showGroupDetails && selectedChat && selectedChat.chatType === "group") {
      fetchAvailableUsers();
    }
  }, [selectedChat, showGroupDetails]);

  useEffect(() => {
    if (!socket) return;

    const handleGroupDeleted = (data) => {
      console.log("Group deleted:", data);
      fetchChatsWithUnreadCount();
      if (selectedChat && selectedChat._id === data.chatId) {
        setSelectedChat(null);
      }
    };

    socket.on("groupDeleted", handleGroupDeleted);

    return () => {
      socket.off("groupDeleted", handleGroupDeleted);
    };
  }, [socket, selectedChat]);

  const shouldShowMessageToUser = (message, chat, username) => {
    // For real-time messages, we can do a simple check
    // The backend already filtered historical messages correctly

    if (message.senderId === "System") {
      return true; // Always show system messages
    }

    if (!chat.participantHistory) {
      return true; // No history, show all messages
    }

    const messageTime = new Date(message.timestamp);

    // Check if user has cleared chat
    const userClearHistory = chat.participantHistory
      .filter(
        (entry) => entry.username === username && entry.action === "clear",
      )
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    if (userClearHistory.length > 0) {
      const lastClearTime = new Date(userClearHistory[0].timestamp);
      if (messageTime <= lastClearTime) {
        return false; // Message is before clear
      }
    }

    // Check if user is currently in the group
    const userHistory = chat.participantHistory
      .filter((entry) => entry.username === username)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    if (userHistory.length > 0) {
      const lastAction = userHistory[0].action;
      // If last action was leaving/being removed, don't show new messages
      if (lastAction === "left" || lastAction === "removed") {
        return false;
      }
    }

    return true;
  };

  const updateNotificationSetting = (chatId, value) => {
    setNotificationSettings((prev) => ({ ...prev, [chatId]: value }));

    // Update the selected chat if it's the current one
    if (selectedChat?._id === chatId) {
      setSelectedChat((prev) => ({
        ...prev,
        notifications:
          typeof prev.notifications === "object"
            ? { ...prev.notifications, [user.username]: value }
            : value,
      }));
    }
  };

  // Update the getVisibleMessages function in Chat.jsx
  // const getVisibleMessages = (messages, chat, currentUsername) => {
  //   if (!chat.participantHistory || chat.participantHistory.length === 0) {
  //     return messages;
  //   }

  //   // Get user's clear history
  //   const userClearHistory = chat.participantHistory
  //     .filter(
  //       (entry) =>
  //         entry.username === currentUsername && entry.action === "clear"
  //     )
  //     .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  //   // If user has cleared the chat, only show messages after the last clear
  //   if (userClearHistory.length > 0) {
  //     const lastClearTime = new Date(userClearHistory[0].timestamp);
  //     return messages.filter(
  //       (message) =>
  //         new Date(message.timestamp) >= lastClearTime ||
  //         message.senderId === "System" // Always show system messages
  //     );
  //   }

  //   // Original logic for other cases (added/removed history)
  //   const userHistory = chat.participantHistory
  //     .filter((entry) => entry.username === currentUsername)
  //     .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  //   // Find the last time user was active in the chat
  //   const lastActiveEntry = userHistory[userHistory.length - 1];
  //   let visibleFrom = new Date(chat.createdAt);

  //   if (lastActiveEntry) {
  //     if (
  //       lastActiveEntry.action === "added" ||
  //       lastActiveEntry.action === "clear"
  //     ) {
  //       visibleFrom = new Date(lastActiveEntry.timestamp);
  //     }
  //   }

  //   return messages.filter((message) => {
  //     const messageTime = new Date(message.timestamp);

  //     // Always show system messages
  //     if (message.senderId === "System") {
  //       return true;
  //     }

  //     return messageTime >= visibleFrom;
  //   });
  // };

  // Replace the fetchMessages function with this improved version:
  const fetchMessages = async (chatId, beforeTimestamp = null) => {
    try {
      // Check if there's a chat cleared system message in current messages
      const hasClearedSystemMessage = messages.some(
        (msg) => msg.senderId === "System" && msg.systemEvent === "chatCleared",
      );

      // If we have a cleared system message and we're trying to load older messages, stop
      if (beforeTimestamp && hasClearedSystemMessage) {
        setHasMoreMessages(false);
        return;
      }

      let url = `${baseUrl}/api/chats/${chatId}/messages`;

      // Build query parameters
      const params = new URLSearchParams();

      // Add pagination parameter if provided (for loading older messages)
      if (beforeTimestamp) {
        params.append("before", beforeTimestamp);
      }

      // Add limit parameter
      params.append("limit", "20");

      // Append params to URL
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axios.get(url, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      });

      // If we're loading older messages and got empty response, no more messages
      if (beforeTimestamp && response.data.length === 0) {
        setHasMoreMessages(false);
        return;
      }

      // Check if any of the fetched messages are older than a chat cleared event
      if (beforeTimestamp) {
        // Find the latest chat cleared system message timestamp
        const clearedSystemMessages = messages.filter(
          (msg) =>
            msg.senderId === "System" && msg.systemEvent === "chatCleared",
        );

        if (clearedSystemMessages.length > 0) {
          const lastClearTime = new Date(clearedSystemMessages[0].timestamp);
          // Filter out messages older than the clear time
          const filteredMessages = response.data.filter(
            (msg) => new Date(msg.timestamp) > lastClearTime,
          );

          if (filteredMessages.length === 0) {
            setHasMoreMessages(false);
            return;
          }

          response.data = filteredMessages;
        }
      }

      if (beforeTimestamp) {
        // Loading older messages - prepend to existing messages
        setShouldAutoScroll(false);

        // Store current scroll position BEFORE adding messages
        const container = messagesContainerRef.current;
        if (container) {
          previousScrollHeightRef.current = container.scrollHeight;
        }

        // Prepend older messages to existing messages
        setMessages((prev) => {
          // Filter out duplicates based on message ID
          const newMessages = response.data.filter(
            (newMsg) =>
              !prev.some((existingMsg) => existingMsg._id === newMsg._id),
          );
          return [...newMessages, ...prev];
        });

        // Scroll position will be adjusted in useEffect after DOM updates
      } else {
        // Initial load - replace all messages and enable auto-scroll
        setIsInitialLoad(true);
        setMessages(response.data || []);
        setHasMoreMessages(true); // Reset for new chat
        setShouldAutoScroll(true);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
      // If there's an error fetching messages, clear the messages and show empty state
      setMessages([]);
      setHasMoreMessages(false);
    }
  };

  // Replace the handleScroll function with this improved version:
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    // Check if user is near the bottom - enable auto-scroll
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

    if (isNearBottom !== shouldAutoScroll) {
      setShouldAutoScroll(isNearBottom);
    }

    // Check if user scrolled to top (with small threshold) to load older messages
    if (
      scrollTop < 100 &&
      messages.length > 0 &&
      !isLoadingMore &&
      hasMoreMessages &&
      !isInitialLoad // Don't trigger during initial load
    ) {
      // Get the timestamp of the oldest message
      const oldestMessage = messages[0];

      // Check if we're trying to load cleared chat system messages
      const isSystemClearedMessage =
        oldestMessage?.senderId === "System" &&
        oldestMessage?.systemEvent === "chatCleared";

      // If the oldest message is the chat cleared system message, don't load more
      if (isSystemClearedMessage) {
        setHasMoreMessages(false);
        return;
      }

      // Debounce the fetch to prevent multiple calls
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }

      fetchTimeoutRef.current = setTimeout(() => {
        const now = Date.now();
        // Prevent fetching too frequently (minimum 500ms between fetches)
        if (now - lastFetchTime < 500) {
          return;
        }

        setLastFetchTime(now);
        setIsLoadingMore(true);

        fetchMessages(selectedChat._id, oldestMessage.timestamp).finally(() => {
          setIsLoadingMore(false);
        });
      }, 300);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/users`, {
        withCredentials: true,
      });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const markAsRead = (messageIds) => {
    if (socket && selectedChat && user) {
      socket.emit("markAsRead", {
        chatId: selectedChat._id,
        messageIds,
        userId: user.username,
      });
      // Option 1: Decrement by the number of messages marked as read
      decrementUnreadCount(messageIds.length);

      // Option 2: Refresh the unread count
      refreshUnreadCount();
    }
  };

  const sendMessage = async () => {
    // ✅ Allow sending if either message or file exists
    if ((!newMessage.trim() && !file) || !socket || !selectedChat || !user)
      return;

    const tempMessageId = `temp-${Date.now()}`;

    // ✅ Create message data with file or content
    const messageData = {
      _id: tempMessageId,
      chatId: selectedChat._id,
      senderId: user.username,
      content: newMessage.trim() || (file ? `📎 ${file.name}` : ""), // Better fallback for file messages
      timestamp: new Date(),
      status: "pending",
      // Add file info for display
      ...(file && {
        file: {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          filePath: null, // Will be updated when upload completes
        },
      }),
    };

    // ✅ UNCOMMENT THIS: Add optimistic message immediately
    setMessages((prev) => [...prev, messageData]);

    // Clear inputs immediately for better UX
    setNewMessage("");
    const currentFile = file; // Store reference before clearing
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setUploading(true);
    let fileData = null;
    try {
      // Handle file upload if present
      if (currentFile) {
        const formData = new FormData();
        formData.append("chatFile", currentFile);
        formData.append("customizedFileName", `${currentFile.name}`);

        const uploadResponse = await axios.post(
          `${baseUrl}/upload/chat`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            withCredentials: true,
          },
        );

        if (uploadResponse.data.success) {
          fileData = {
            filePath: uploadResponse.data.filePath,
            fileName: uploadResponse.data.fileName,
            fileType: uploadResponse.data.fileType,
            fileSize: currentFile.size,
          };
        }
      }

      // Prepare final message data for socket
      const finalMessageData = {
        _id: tempMessageId,
        chatId: selectedChat._id,
        senderId: user.username,
        content: messageData.content, // Use the content we prepared above
        timestamp: new Date(),
      };

      // Add file data if available
      if (fileData) {
        finalMessageData.file = fileData;

        // Update the optimistic message with real file data
        setMessages((prev) =>
          prev.map((msg) =>
            msg._id === tempMessageId
              ? { ...msg, file: fileData, status: "sent" }
              : msg,
          ),
        );
      }

      // Emit the message to the server
      socket.emit("sendMessage", finalMessageData);
    } catch (error) {
      console.error("Error sending message:", error);

      // ✅ NEW: Clean up uploaded file if socket emit fails
      if (fileData && fileData.fileName) {
        try {
          await axios.delete(`${baseUrl}/upload/chat/${fileData.fileName}`, {
            withCredentials: true,
          });
        } catch (deleteError) {
          console.error("Error deleting orphaned file:", deleteError);
        }
      }

      // Remove the optimistic message on error and restore the inputs
      setMessages((prev) => prev.filter((msg) => msg._id !== tempMessageId));

      // Restore the inputs on error
      if (currentFile && !newMessage.trim()) {
        setFile(currentFile);
      }
      if (
        messageData.content &&
        messageData.content !== `📎 ${currentFile?.name}`
      ) {
        setNewMessage(messageData.content);
      }

      showModal("Error", "Failed to send message. Please try again.", "error");
    } finally {
      setUploading(false);
    }
  };

  const createNewChat = async (participant) => {
    try {
      // First, check if there's an existing chat (even if deleted)
      const existingChatsResponse = await axios.get(`${baseUrl}/api/chats`, {
        withCredentials: true,
      });

      const existingChat = existingChatsResponse.data.find(
        (chat) =>
          chat.chatType === "private" &&
          chat.participants.includes(user.username) &&
          chat.participants.includes(participant.username) &&
          chat.participants.length === 2,
      );

      if (existingChat) {
        // Check if this chat was previously deleted
        const userHistory = existingChat.participantHistory
          ?.filter((entry) => entry.username === user.username)
          ?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        const wasDeleted =
          userHistory &&
          userHistory.length > 0 &&
          userHistory[0].action === "delete";

        if (wasDeleted) {
          // Restore the chat by adding an 'added' action
          const response = await axios.post(
            `${baseUrl}/api/chats/restore`,
            {
              chatId: existingChat._id,
              username: user.username,
            },
            { withCredentials: true },
          );

          setSelectedChat(response.data);
          fetchChatsWithUnreadCount();
          return;
        }

        // If chat exists and is active, just select it
        setSelectedChat(existingChat);
        return;
      }

      // Create new chat if no existing chat found
      const response = await axios.post(
        `${baseUrl}/api/create-chats`,
        {
          participants: [user.username, participant.username],
        },
        { withCredentials: true },
      );
      console.log("New chat created:", response.data);
      addNewChat(response.data);
      fetchChatsWithUnreadCount();
      setSelectedChat(response.data);
      setSearchTerm("");
    } catch (error) {
      console.error("Error creating chat:", error);
    }
  };

  const handleTyping = () => {
    if (!socket || !selectedChat || !user) return;

    if (!isTyping) {
      socket.emit("typing", {
        chatId: selectedChat._id,
        userId: user.username,
        isTyping: true,
      });
      setIsTyping(true);
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      socket.emit("typing", {
        chatId: selectedChat._id,
        userId: user.username,
        isTyping: false,
      });
      setIsTyping(false);
    }, 1000);
  };

  // Update the scrollToBottom function to be more reliable:
  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      // Use requestAnimationFrame for smoother scrolling
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 1 * 1024 * 1024) {
        showModal("File Too Large", "File size must be less than 1MB", "error");
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const removeFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const downloadFile = async (filename) => {
    try {
      const response = await axios.get(`${baseUrl}/chat/download/${filename}`, {
        responseType: "blob",
        withCredentials: true,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        showModal("File Not Found", "File does not exist.", "error");
        setUnavailableFiles((prev) => [...prev, filename]); // mark file unavailable
      } else {
        console.error("Error downloading file:", error);
        showModal(
          "Download Error",
          "Something went wrong while downloading.",
          "error",
        );
      }
    }
  };

  if (!isAuthenticated || !user) {
    return <div className="auth-required">Please log in to use the chat</div>;
  }

  const filteredUsers = users.filter(
    (userItem) =>
      userItem.username.includes(searchTerm.toLowerCase()) &&
      !chats.some(
        (chat) =>
          chat.participants.includes(userItem.username) &&
          chat.participants.includes(user.username) &&
          chat.participants.length === 2,
      ),
  );

  // console.log("Filtered users:>>>>", filteredUsers);
  const otherParticipant = selectedChat?.participants?.find?.(
    (p) => p !== user.username,
  );

  //Group Chat Functions
  const getUserProfile = (username) => {
    // Find the user in the users array
    const userProfile = users.find((user) => user.username === username);

    // Return the profile field if it exists, otherwise return a default structure
    return userProfile
      ? {
          username: userProfile.username,
          // profile: userProfile.profile || `${userProfile.username}'s profile`,
          // email: userProfile.email,
          avatar: userProfile.avatar || userProfile.username[0],
        }
      : {
          username: username,
          // profile: `${username}'s profile`,
          avatar: username[0],
        };
  };

  const createGroupChat = async () => {
    try {
      if (!groupName.trim() || selectedUsers.length < 2) return;

      const response = await axios.post(
        `${baseUrl}/api/create-chats`,
        {
          participants: selectedUsers.map((u) => u.username),
          chatName: groupName.trim(),
          chatType: "group",
        },
        { withCredentials: true },
      );
      console.log("Group chat created:", response.data);
      fetchChatsWithUnreadCount();
      setSelectedChat(response.data);
      setShowCreateGroup(false);
      setGroupName("");
      setSelectedUsers([]);
    } catch (error) {
      console.error("Error creating group chat:", error);
      showModal(
        "Error",
        "Error creating group chat: " +
          (error.response?.data?.message || error.message),
        "error",
      );
    }
  };

  // Add this function to handle user selection for group
  const toggleUserSelection = (user) => {
    setSelectedUsers((prev) => {
      if (prev.some((u) => u._id === user._id)) {
        return prev.filter((u) => u._id !== user._id);
      } else {
        return [...prev, user];
      }
    });
  };

  // Group management functions
  // Fetch available users to add to group
  const fetchAvailableUsers = async () => {
    try {
      // Use the existing users data instead of making another API call
      const available = users.filter(
        (userItem) =>
          !selectedChat.participants.includes(userItem.username) &&
          userItem.username !== user.username,
      );
      setAvailableUsers(available);
    } catch (error) {
      console.error("Error filtering available users:", error);
    }
  };

  // Add participant to group
  const addParticipant = async (username) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/chats/group/add`,
        {
          chatId: selectedChat._id,
          username: username,
          addedBy: user.username,
        },
        { withCredentials: true },
      );

      console.log("Add participant response:", response.data);
    } catch (error) {
      console.error("Error adding participant:", error);
      showModal(
        "Error",
        "Error adding participant: " +
          (error.response?.data?.message || error.message),
        "error",
      );
    }
  };

  // Remove participant from group
  const removeParticipant = async (username) => {
    try {
      if (username === user.username) {
        // User is removing themselves - use leave API
        const success = await leaveGroupChat(selectedChat._id);
        if (success && socket) {
          socket.emit("leaveGroup", {
            chatId: selectedChat._id,
            userId: user.username,
          });
        }
        return;
      }

      // For removing other users
      const response = await axios.post(
        `${baseUrl}/api/chats/group/remove`,
        {
          chatId: selectedChat._id,
          username: username,
          removedBy: user.username,
        },
        { withCredentials: true },
      );

      console.log("Remove participant response:", response.data);

      // The socket event will handle UI updates
    } catch (error) {
      console.error("Error removing participant:", error);
      showModal(
        "Error",
        "Error removing participant: " +
          (error.response?.data?.message || error.message),
        "error",
      );
    }
  };

  const getGroupCreator = () => {
    // Fallback to the first participant if creator doesn't exist
    return selectedChat.creator || selectedChat.participants[0];
  };

  // Helper function to get file icon based on file type
  const getFileIcon = (fileName) => {
    const extension = getFileExtension(fileName).toLowerCase();
    console.log("File extension:", extension);
    // Image files
    if (
      ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"].includes(extension)
    ) {
      return (
        <div className="file-icon-wrapper image-icon">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21,15 16,10 5,21" />
          </svg>
        </div>
      );
    }

    // Document files
    if (["pdf"].includes(extension)) {
      return (
        <div className="file-icon-wrapper pdf-icon">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
          </svg>
        </div>
      );
    }

    // Word documents
    if (["doc", "docx"].includes(extension)) {
      return (
        <div className="file-icon-wrapper word-icon">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
            <text
              x="12"
              y="16"
              fontSize="6"
              textAnchor="middle"
              fill="currentColor"
            >
              W
            </text>
          </svg>
        </div>
      );
    }

    // Excel files
    if (["xls", "xlsx"].includes(extension)) {
      return (
        <div className="file-icon-wrapper excel-icon">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
            <text
              x="12"
              y="16"
              fontSize="6"
              textAnchor="middle"
              fill="currentColor"
            >
              X
            </text>
          </svg>
        </div>
      );
    }

    // Video files
    if (["mp4", "avi", "mkv", "mov", "wmv"].includes(extension)) {
      return (
        <div className="file-icon-wrapper video-icon">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polygon points="23 7 16 12 23 17 23 7" />
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
          </svg>
        </div>
      );
    }

    // Audio files
    if (["mp3", "wav", "flac", "aac"].includes(extension)) {
      return (
        <div className="file-icon-wrapper audio-icon">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M9 18V5l12-2v13" />
            <circle cx="6" cy="18" r="3" />
            <circle cx="18" cy="16" r="3" />
          </svg>
        </div>
      );
    }

    // Default file icon
    return (
      <div className="file-icon-wrapper default-icon">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
        </svg>
      </div>
    );
  };

  // Helper function to get file extension
  const getFileExtension = (fileName) => {
    return fileName.split(".").pop() || "";
  };

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    // Handle undefined, null, or invalid values
    if (!bytes || bytes === 0 || isNaN(bytes)) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    // Ensure we don't go out of bounds
    const sizeIndex = Math.min(i, sizes.length - 1);

    return (
      parseFloat((bytes / Math.pow(k, sizeIndex)).toFixed(2)) +
      " " +
      sizes[sizeIndex]
    );
  };

  // Format date separator text (Today, Yesterday, or full date)
  const formatDateSeparator = (dateString) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const messageDate = new Date(dateString);

    if (messageDate.toDateString() === today.toDateString()) {
      return "Today";
    } else if (messageDate.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return messageDate.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
  };

  const getMessagesWithDateSeparators = () => {
    if (messages.length === 0) return [];

    const messagesWithSeparators = [];
    let lastDate = null;

    messages.forEach((message, index) => {
      const messageDate = new Date(message.timestamp).toDateString();

      // Add date separator if this is the first message or date has changed
      if (lastDate !== messageDate) {
        messagesWithSeparators.push({
          _id: `date-${messageDate}-${index}`, // ✅ Use message index for uniqueness
          type: "dateSeparator",
          date: messageDate,
          timestamp: new Date(),
        });
        lastDate = messageDate;
      }

      // Add the actual message
      messagesWithSeparators.push(message);
    });

    return messagesWithSeparators;
  };

  const messagesWithSeparators = getMessagesWithDateSeparators();

  const softDeleteChat = async (chatId) => {
    try {
      const response = await axios.patch(
        `${baseUrl}/chats/${chatId}/delete`,
        { username: user.username },
        { withCredentials: true },
      );

      if (response.data.success) {
        // Remove the chat from the list
        fetchChatsWithUnreadCount();

        // If this was the selected chat, deselect it
        if (selectedChat && selectedChat._id === chatId) {
          setSelectedChat(null);
        }

        return true;
      }
    } catch (error) {
      console.error("Error soft deleting chat:", error);
      showModal("Error", "Failed to delete chat. Please try again.", "error");
      return false;
    }
  };

  const leaveGroupChat = async (chatId) => {
    try {
      const response = await axios.patch(
        `${baseUrl}/chats/${chatId}/leave`,
        { username: user.username },
        { withCredentials: true },
      );

      if (response.data.success) {
        // Add system message for the user who left
        if (selectedChat && selectedChat._id === chatId) {
          const systemMessage = {
            _id: `system-${Date.now()}`,
            chatId: chatId,
            senderId: "System",
            content: "You left the group",
            timestamp: new Date(),
            read: true,
            systemEvent: "userLeft",
          };
          setMessages((prev) => [...prev, systemMessage]);
        }

        // Remove chat after delay
        setTimeout(() => {
          fetchChatsWithUnreadCount();
          if (selectedChat && selectedChat._id === chatId) {
            setSelectedChat(null);
          }
        }, 1000);
        fetchMessages(chatId);
        // Emit socket event
        if (socket) {
          socket.emit("leaveGroup", {
            chatId: chatId,
            userId: user.username,
          });
        }

        return true;
      }
    } catch (error) {
      console.error("Error leaving group:", error);
      showModal("Error", "Failed to leave group. Please try again.", "error");
      return false;
    }
  };

  const deleteChat = async () => {
    if (!selectedChat || !user) return;

    try {
      // Use the same softDeleteChat function for both, backend handles the difference
      await softDeleteChat(selectedChat._id);
      // Deleted un necessary socket call
      setShowMenu(false);

      // For individual chats, deselect the chat; for groups, keep it selected
      if (selectedChat.chatType === "private") {
        setSelectedChat(null);
      }
      // For groups, the chat remains selected but messages are cleared
    } catch (error) {
      console.error("Error deleting/clearing chat:", error);
      showModal(
        "Error",
        "Failed to delete/clear chat. Please try again.",
        "error",
      );
    }
  };

  // Add this function to handle mute/unmute
  const toggleMute = async () => {
    if (!selectedChat || !user || muting) return;

    const chatId = selectedChat._id;
    const currentSetting = notificationSettings[chatId] !== false;
    const newSetting = !currentSetting;

    setMuting(true);

    try {
      // Optimistic update
      updateNotificationSetting(chatId, newSetting);

      // API call
      const { data } = await axios.post(
        `${baseUrl}/api/chats/mute`,
        {
          chatId,
          notifications: newSetting,
        },
        { withCredentials: true },
      );

      // Update the selected chat with the returned data
      if (data.updatedChat) {
        setSelectedChat((prev) => ({
          ...prev,
          notifications: data.updatedChat.notifications,
        }));
      }

      // Also update the notification settings with the actual response
      if (data.notifications) {
        setNotificationSettings((prev) => ({
          ...prev,
          [chatId]: data.notifications[user.username] !== false,
        }));
      }

      setShowMenu(false);
    } catch (err) {
      console.error("Error updating notifications:", err);
      // Revert on error
      updateNotificationSetting(chatId, currentSetting);
      showModal(
        "Error",
        "Failed to update notification settings. Please try again.",
        "error",
      );
    } finally {
      setMuting(false);
    }
  };

  const clearChat = async (chatId) => {
    try {
      if (!socket || !user) return;

      socket.emit("clearChat", {
        chatId,
        userId: user.username,
      });
      setShowMenu(false);
      if (selectedChat && selectedChat._id === chatId) {
        setMessages([]);
      }
    } catch (error) {
      console.error("Error clearing chat:", error);
      showModal("Error", "Failed to clear chat. Please try again.", "error");
    }
  };

  const getUserHistoryStatus = (username) => {
    if (!selectedChat.participantHistory) return null;

    const userHistory = selectedChat.participantHistory
      .filter((entry) => entry.username === username)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    if (userHistory.length > 0) {
      const lastAction = userHistory[0].action;
      switch (lastAction) {
        case "added":
          return "Recently added";
        case "removed":
          return "Previously removed";
        case "left":
          return "Left group";
        default:
          return null;
      }
    }
    return null;
  };

  // In Chat.jsx - update shouldDisplayChat function
  const shouldDisplayChat = (chat, currentUsername) => {
    // For individual chats
    if (chat.chatType === "private") {
      const currentUserHistory = chat.participantHistory
        ?.filter((entry) => entry.username === currentUsername)
        ?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      // Current user is active if their last action is 'added' or no history (never deleted)
      const isCurrentUserActive =
        !currentUserHistory ||
        currentUserHistory.length === 0 ||
        currentUserHistory[0].action === "added";

      // Only show chat if current user hasn't deleted it
      return isCurrentUserActive;
    }

    // For group chats, current user must be active
    const userHistory = chat.participantHistory
      ?.filter((entry) => entry.username === currentUsername)
      ?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    const isActive =
      !userHistory ||
      userHistory.length === 0 ||
      userHistory[0].action === "added" ||
      userHistory[0].action === "clear";

    return isActive;
  };

  // Update the getChatDisplayName function to handle "deleted" users better
  const getChatDisplayName = (chat, currentUsername) => {
    if (chat.chatType === "group") {
      return chat.chatName;
    }

    // For individual chats, find the other participant
    const otherUser = chat.participants?.find((p) => p !== currentUsername);

    // Always return the actual username, never "Unknown User" or "Deleted User"
    return otherUser || "User";
  };

  // Add the hardDeleteGroup function
  const hardDeleteGroup = async (chatId) => {
    try {
      if (!socket || !user) return;

      socket.emit("hardDeleteGroup", {
        chatId: chatId,
        userId: user.username,
      });

      // Listen for success/error
      socket.once("hardDeleteGroupSuccess", (data) => {
        console.log("Group hard deleted:", data);
        fetchChatsWithUnreadCount();
        setSelectedChat(null);
      });

      socket.once("hardDeleteGroupError", (data) => {
        console.error("Error hard deleting group:", data.error);
        showModal("Error", `Failed to delete group: ${data.error}`, "error");
      });
    } catch (error) {
      console.error("Error hard deleting group:", error);
      showModal("Error", "Failed to delete group. Please try again.", "error");
    }
  };

  const formatChatTime = (time) => {
    const now = new Date();

    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() - 1,
    );

    const messageDate = new Date(
      time.getFullYear(),
      time.getMonth(),
      time.getDate(),
    );

    if (messageDate.getTime() === today.getTime()) {
      return time.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (messageDate.getTime() === yesterday.getTime()) {
      return "Yesterday";
    } else if ((now - time) / (1000 * 60 * 60 * 24) < 7) {
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      return days[time.getDay()];
    } else {
      return `${time.getDate()}/${time.getMonth() + 1}/${time.getFullYear()}`;
    }
  };

  return (
    <div id="chat-page">
      <div className="chat-container">
        <div className="chat-sidebar">
          <div className="chat-header">
            {/* <h2>Chats</h2> */}
            <button
              onClick={() => setShowCreateGroup(true)}
              className="create-group-btn"
            >
              <HiUserGroup /> Create Group
            </button>
            <div className="connection-status">
              <span
                className={`status-indicator ${
                  isConnected ? "connected" : "disconnected"
                }`}
              ></span>
              {isConnected ? "Connected" : "Disconnected"}
            </div>
          </div>

          <div className="search-container">
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />

            {searchTerm && (
              <div className="search-results">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((userItem) => (
                    <div
                      key={userItem._id}
                      className="search-result-item"
                      onClick={() => createNewChat(userItem)}
                    >
                      <div className="user-avatar">{userItem.username[0]}</div>
                      <div className="user-info">
                        <div className="username">{userItem.username}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="search-no-results">No users found</div>
                )}
              </div>
            )}
          </div>
          <div className="chats-list">
            {chats
              .filter((chat) => shouldDisplayChat(chat, user.username))
              .map((chat) => {
                const displayName = getChatDisplayName(chat, user.username);

                // Safe avatar generation
                const avatar =
                  chat.chatType === "group"
                    ? "👥"
                    : displayName?.[0]?.toUpperCase() || "U";

                return (
                  <div
                    key={chat._id}
                    className={`chat-item ${
                      selectedChat?._id === chat._id ? "active" : ""
                    }`}
                    onClick={() => setSelectedChat(chat)}
                  >
                    <div className="chat-avatar">{avatar}</div>
                    <div className="chat-info">
                      <div className="chat-name">{displayName}</div>
                      <div className="last-message">
                        {chat.lastMessage || "No messages yet"}
                      </div>
                    </div>
                    <div className="chat-meta">
                      <div className="timestamp">
                        <div className="timestamp">
                          {chat.lastMessageTime
                            ? formatChatTime(new Date(chat.lastMessageTime))
                            : ""}
                        </div>
                      </div>
                      {chat.unreadCount > 0 && (
                        <div className="unread-badge">{chat.unreadCount}</div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
        <div className="chat-main">
          {selectedChat ? (
            <>
              <div className="chat-header">
                <div className="chat-partner">
                  <div className="partner-avatar">
                    {selectedChat.chatType === "group"
                      ? "👥"
                      : otherParticipant?.[0]?.toUpperCase() || "U"}
                  </div>
                  <div className="partner-info">
                    <div className="partner-name">
                      {selectedChat.chatType === "group"
                        ? selectedChat.chatName
                        : otherParticipant || "User"}
                    </div>
                    <div className="partner-status">
                      {Object.entries(typingUsers).some(
                        ([userId, isTyping]) =>
                          isTyping && userId !== user.username,
                      )
                        ? "typing..."
                        : selectedChat.chatType === "group"
                          ? `${selectedChat.participants?.length || 0} members`
                          : "Online"}
                    </div>
                  </div>
                </div>

                {/* Three-dot menu button */}
                <div className="chat-header-menu">
                  <button
                    className="menu-toggle"
                    onClick={() => setShowMenu(!showMenu)}
                  >
                    <HiDotsVertical />
                  </button>

                  {showMenu && (
                    <div className="chat-menu-dropdown">
                      {/* Mute/Unmute option */}
                      <div
                        className={`menu-item ${muting ? "disabled" : ""}`}
                        onClick={!muting ? toggleMute : undefined}
                        aria-disabled={muting}
                        role="button"
                      >
                        <span>
                          {notificationSettings[selectedChat._id] === false
                            ? "🔕 Unmute"
                            : "🔔 Mute"}
                        </span>
                        {muting && <span className="muting-spinner">...</span>}
                      </div>

                      {/* Clear Chat for Everyone (Group Creator only) */}
                      {selectedChat.chatType === "group" &&
                        selectedChat.creator === user.username && (
                          <>
                            <div
                              className="menu-item"
                              onClick={() => {
                                clearChat(selectedChat._id);
                                setShowMenu(false);
                              }}
                            >
                              <HiOutlineTrash />
                              <span>Clear Chat for Everyone</span>
                            </div>
                            <div
                              className="menu-item danger"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    "This will permanently delete the group and all messages for everyone. This action cannot be undone. Are you sure?",
                                  )
                                ) {
                                  hardDeleteGroup(selectedChat._id);
                                }
                                setShowMenu(false);
                              }}
                            >
                              <HiTrash />
                              <span>Delete Group Permanently</span>
                            </div>
                          </>
                        )}

                      {/* Delete/Clear Messages option - Different behavior based on chat type */}
                      <div className="menu-item" onClick={deleteChat}>
                        <HiTrash />
                        <span>
                          {selectedChat.chatType === "group"
                            ? "Clear Messages"
                            : "Clear Chat"}
                        </span>
                      </div>

                      {/* Group-specific options */}
                      {selectedChat.chatType === "group" && (
                        <>
                          <div
                            className="menu-item"
                            onClick={() => {
                              leaveGroupChat(selectedChat._id);
                              setShowMenu(false);
                            }}
                          >
                            <HiOutlineLogout />
                            <span>Leave Group</span>
                          </div>
                          <div
                            className="menu-item"
                            onClick={() => {
                              setShowGroupDetails(true);
                              setShowMenu(false);
                            }}
                          >
                            <HiCog />
                            <span>Manage Group</span>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {/* Group management buttons */}
              {showGroupDetails && selectedChat.chatType === "group" && (
                <div className="group-details-modal">
                  <div className="group-details-content">
                    <div className="group-details-header">
                      <h2>{selectedChat.chatName}</h2>
                      <button
                        onClick={() => setShowGroupDetails(false)}
                        className="close-modal-btn"
                      >
                        ×
                      </button>
                    </div>

                    <div className="group-info-section">
                      <div className="group-creator">
                        <span className="info-label">Created by:</span>
                        <span className="creator-name">
                          {getGroupCreator()}
                        </span>
                      </div>
                      <div className="group-stats">
                        <span className="info-label">Members:</span>
                        <span className="members-count">
                          {selectedChat.participants.length}{" "}
                        </span>
                      </div>
                    </div>

                    <div className="participants-section">
                      <h3>Participants</h3>
                      <div className="participants-list">
                        {selectedChat.participants.map((participant) => {
                          const userProfile = getUserProfile(participant);
                          return (
                            <div key={participant} className="participant-card">
                              <div className="participant-avatar">
                                {userProfile.avatar}
                              </div>
                              <div className="participant-info">
                                <div className="participant-name">
                                  {userProfile.username}
                                </div>
                                {userProfile.profile && (
                                  <div className="participant-profile">
                                    {userProfile.profile}
                                  </div>
                                )}
                              </div>
                              {participant !== user.username &&
                                participant !== getGroupCreator() && (
                                  <div className="participant-actions">
                                    <button
                                      onClick={() =>
                                        removeParticipant(participant)
                                      }
                                      className="remove-participant-btn"
                                      title="Remove from group"
                                    >
                                      Remove
                                    </button>
                                    {/* Show historical status if available */}
                                    {chats.participantHistory && (
                                      <span className="history-status">
                                        {getUserHistoryStatus(participant)}
                                      </span>
                                    )}
                                  </div>
                                )}
                              {participant === getGroupCreator() && (
                                <span className="creator-badge">Creator</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="add-participant-section">
                      <h3>Add Participants</h3>
                      <input
                        type="text"
                        placeholder="Search users to add..."
                        value={groupSearchTerm}
                        onChange={(e) => setGroupSearchTerm(e.target.value)}
                        className="add-participant-search"
                      />
                      <div className="available-users-grid">
                        {availableUsers
                          .filter((userItem) =>
                            userItem.username
                              .toLowerCase()
                              .includes(groupSearchTerm.toLowerCase()),
                          )
                          .map((userItem) => {
                            const userProfile = getUserProfile(
                              userItem.username,
                            );
                            return (
                              <div key={userItem._id} className="user-card">
                                <div className="user-avatar">
                                  {userProfile.avatar}
                                </div>
                                <div className="user-info">
                                  <div className="user-name">
                                    {userProfile.username}
                                  </div>
                                  {userProfile.profile && (
                                    <div className="user-profile">
                                      {userProfile.profile}
                                    </div>
                                  )}
                                  {userProfile.email && (
                                    <div className="user-email">
                                      {userProfile.email}
                                    </div>
                                  )}
                                </div>
                                <button
                                  onClick={() =>
                                    addParticipant(userItem.username)
                                  }
                                  className="add-user-btn"
                                  title="Add to group"
                                >
                                  Add
                                </button>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div
                className="messages-container"
                ref={messagesContainerRef}
                onScroll={handleScroll}
              >
                {isLoadingMore && (
                  <div className="loading-more-messages">
                    Loading older messages...
                  </div>
                )}

                {!hasMoreMessages && messages.length > 0 && (
                  <div className="no-more-messages">No older messages</div>
                )}

                {messagesWithSeparators.map((item) => {
                  if (item.type === "dateSeparator") {
                    return (
                      <div key={item._id} className="date-separator">
                        <span>{formatDateSeparator(item.date)}</span>
                      </div>
                    );
                  }

                  const message = item;
                  const isSystemMessage = message.senderId === "System";

                  return (
                    <div
                      key={message._id}
                      className={`message ${
                        isSystemMessage
                          ? "system"
                          : message.senderId === user.username
                            ? "sent"
                            : "received"
                      } ${message.status === "pending" ? "pending" : ""}`}
                    >
                      <div className="message-content">
                        {/* Show sender name for group chats (except system messages) */}
                        {selectedChat.chatType === "group" &&
                          message.senderId !== user.username &&
                          !isSystemMessage && (
                            <div className="message-sender">
                              {message.senderId}
                            </div>
                          )}

                        {message.file ? (
                          <div className="file-message-card">
                            <div className="file-card">
                              <div className="file-icon">
                                {getFileIcon(
                                  message.file.fileName,
                                  message.file.fileType,
                                  message.file.fileSize,
                                )}
                              </div>
                              <div className="file-details">
                                <div className="file-name">
                                  {message.file.fileName}
                                </div>
                                <div className="file-meta">
                                  <span className="file-size">
                                    {message.file.fileSize &&
                                    message.file.fileSize > 0
                                      ? formatFileSize(message.file.fileSize)
                                      : "Size unknown"}
                                  </span>
                                  <span className="file-type">
                                    {getFileExtension(
                                      message.file.fileName,
                                    ).toUpperCase() || "FILE"}
                                  </span>
                                </div>
                                {message.status === "pending" && (
                                  <div className="upload-progress">
                                    <div className="progress-bar">
                                      <div className="progress-fill"></div>
                                    </div>
                                    <span className="upload-text">
                                      Uploading...
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div className="file-actions">
                                {message.status !== "pending" && (
                                  <button
                                    className={`download-btn ${
                                      unavailableFiles.includes(
                                        message.file.fileName,
                                      )
                                        ? "disabled"
                                        : ""
                                    }`}
                                    onClick={() =>
                                      !unavailableFiles.includes(
                                        message.file.fileName,
                                      ) && downloadFile(message.file.fileName)
                                    }
                                    title={
                                      unavailableFiles.includes(
                                        message.file.fileName,
                                      )
                                        ? "File does not exist"
                                        : "Download file"
                                    }
                                    disabled={unavailableFiles.includes(
                                      message.file.fileName,
                                    )}
                                  >
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    >
                                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                      <polyline points="7,10 12,15 17,10" />
                                      <line x1="12" y1="15" x2="12" y2="3" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Show text content if it exists along with file */}
                            {message.content &&
                              !message.content.startsWith("📎") && (
                                <div className="file-message-text">
                                  <p>{message.content}</p>
                                </div>
                              )}
                          </div>
                        ) : (
                          <>
                            {isSystemMessage ? (
                              <div className="system-message-content">
                                {/* <span className="system-icon">⚙️</span> */}
                                <span>{message.content}</span>
                              </div>
                            ) : (
                              <p>{message.content}</p>
                            )}
                          </>
                        )}

                        {/* Don't show timestamp for system messages */}
                        {!isSystemMessage && (
                          <div className="message-time">
                            {new Date(message.timestamp).toLocaleTimeString(
                              [],
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                            {message.status === "pending" && " • Sending..."}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              <div className="message-input-container">
                {file && (
                  <div className="file-preview">
                    <span>📎 {file.name}</span>
                    <button onClick={removeFile}>×</button>
                  </div>
                )}
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => {
                      setNewMessage(e.target.value);
                      handleTyping();
                    }}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    className="message-input"
                    disabled={uploading}
                  />
                  <button
                    onClick={handleFileButtonClick}
                    className="file-upload-btn"
                    disabled={uploading}
                  >
                    📎
                  </button>
                  <input
                    ref={fileInputRef}
                    id="file-upload"
                    type="file"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                    disabled={uploading}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={uploading || (!newMessage.trim() && !file)}
                    className="send-btn"
                  >
                    {uploading ? "⏳" : "➤"}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="no-chat-selected">
              <div className="no-chat-content">
                <h3>Select a chat to start messaging</h3>
                <p>Or search for users to start a new conversation</p>
              </div>
            </div>
          )}
        </div>

        {/* Group Chat Creation Modal */}
        {showCreateGroup && (
          <AlertModal
            isOpen={modalConfig.isOpen}
            onClose={closeModal}
            title={modalConfig.title}
            message={modalConfig.message}
            type={modalConfig.type}
          />
        )}
        {showCreateGroup && (
          <div className="group-creation-modal">
            <div className="modal-content">
              <h3>Create Group Chat</h3>
              <input
                type="text"
                placeholder="Group Name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
              />
              <div className="users-list">
                {users
                  .filter((u) => u.username !== user.username)
                  .map((userItem) => (
                    <div
                      key={userItem._id}
                      className={`user-item ${
                        selectedUsers.some((u) => u._id === userItem._id)
                          ? "selected"
                          : ""
                      }`}
                      onClick={() => toggleUserSelection(userItem)}
                    >
                      {userItem.username}
                    </div>
                  ))}
              </div>
              <button
                onClick={createGroupChat}
                disabled={!groupName || selectedUsers.length < 2}
              >
                Create Group
              </button>
              <button onClick={() => setShowCreateGroup(false)}>Cancel</button>
            </div>
          </div>
        )}
        {notification && (
          <div className="notification-toast">{notification}</div>
        )}
      </div>
    </div>
  );
};

export default Chat;
